import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { TopologyArtifactView } from '@app/components/TopologyArtifactView';
import { useConfig } from '@config/provider';
import { TopologyV2Familyschema, TopologyV2ScopeTypeschema } from '@app/types/sdk';
import type { TopologyV2ResolveRequest, TopologyV2ResolveResponse } from '@app/types/sdk';
import { buildTopologyOverlayBase, type TopologyArtifactContent } from '@app/utils/topologyArtifact';

type RuntimeTone = 'success' | 'warning' | 'critical';

const siteScope = { scopeType: TopologyV2ScopeTypeschema.Site, scopeId: 'site-hq' };
const viewId = 'physical_overview';
const now = new Date('2026-05-26T16:00:00.000Z');

const baseNodes = [
  {
    id: 'dist1',
    displayName: 'Dist1',
    labels: { level: 'device', domain: 'physical', family: TopologyV2Familyschema.Physical, role: 'Distribution' },
    refs: [{ type: 'inventory.device', id: 'device-dist1' }]
  },
  {
    id: 'access1',
    displayName: 'Access1',
    labels: { level: 'device', domain: 'physical', family: TopologyV2Familyschema.Physical, role: 'Access' },
    refs: [{ type: 'inventory.device', id: 'device-access1' }]
  },
  {
    id: 'access2',
    displayName: 'Access2',
    labels: { level: 'device', domain: 'physical', family: TopologyV2Familyschema.Physical, role: 'Access' },
    refs: [{ type: 'inventory.device', id: 'device-access2' }]
  }
];

const makeTopologyContent = (
  title: string,
  timeMode: 'fixed' | 'current',
  overlayState?: Record<string, unknown>
): TopologyArtifactContent =>
  ({
    type: 'topology',
    version: 'v1',
    title,
    binding: {
      source: 'topology-service',
      viewId,
      scope: siteScope,
      limits: { limitNodes: 100, limitEdges: 200 },
      timeMode,
      ...(timeMode === 'fixed' ? { asOf: now.toISOString() } : {})
    },
    presentation: {
      renderer: 'topology-v2',
      ...(overlayState ? { overlayState } : {})
    }
  }) as TopologyArtifactContent;

const makeOverlayState = (
  content: TopologyArtifactContent,
  layer: { id: string; kind: 'metric' | 'state'; label: string; deliveryMode: 'snapshot' | 'live'; catalogIds: string[] }
) => ({
  schema: 'topology.overlay.state.v1',
  base: buildTopologyOverlayBase(content),
  revision: layer.deliveryMode === 'live' ? 2 : 1,
  selection: { nodeIds: [], edgeIds: [] },
  layers: [
    {
      id: layer.id,
      kind: layer.kind,
      label: layer.label,
      priority: 10,
      visible: true,
      runtime: { deliveryMode: layer.deliveryMode, catalogIds: layer.catalogIds },
      meta: { source: 'mock', effects: ['node_badge', 'node_ring', 'node_color', 'edge_label', 'edge_color', 'hover_rows'] }
    }
  ],
  graphOverlays: []
});

const snapshotBase = makeTopologyContent('HQ topology - snapshot runtime overlays', 'fixed');
const snapshotTopology = makeTopologyContent(
  'HQ topology - snapshot runtime overlays',
  'fixed',
  makeOverlayState(snapshotBase, {
    id: 'layer-snapshot-health',
    kind: 'metric',
    label: 'Snapshot health metrics',
    deliveryMode: 'snapshot',
    catalogIds: ['metrics.snmp.device.cpu_pct', 'metrics.snmp.interface.utilization_pct']
  })
);

const liveBase = makeTopologyContent('HQ topology - live runtime overlays', 'current');
const liveTopology = makeTopologyContent(
  'HQ topology - live runtime overlays',
  'current',
  makeOverlayState(liveBase, {
    id: 'layer-live-utilization',
    kind: 'metric',
    label: 'Live utilization',
    deliveryMode: 'live',
    catalogIds: ['metrics.snmp.interface.utilization_pct']
  })
);

const pathBase = makeTopologyContent('HQ topology - path overlay', 'fixed');
const pathOverlayTopology = makeTopologyContent('HQ topology - path overlay', 'fixed', {
  schema: 'topology.overlay.state.v1',
  base: buildTopologyOverlayBase(pathBase),
  revision: 1,
  selection: { nodeIds: [], edgeIds: [] },
  layers: [
    {
      id: 'layer-path-overlay-h1-h2',
      kind: 'path',
      label: 'L3 reachability path H1 to H2',
      priority: 160,
      visible: true,
      path: {
        pathId: 'path-h1-h2-primary',
        resultScopeId: 'path_result:h1-h2',
        requestedMode: 'auto',
        selectedMode: 'l3_reachability',
        evidenceLevel: 'forwarding_supported',
        sourceSummary: 'Host H1',
        destinationSummary: 'Host H2',
        renderDecision: {
          presentation: 'overlay',
          detailLevel: 'summary',
          reason: 'All path targets are present in the current physical overview.',
          layerId: 'layer-path-overlay-h1-h2'
        }
      },
      targets: [
        { type: 'node', id: 'dist1' },
        { type: 'node', id: 'access1' },
        { type: 'edge', id: 'edge-dist-access1' }
      ],
      effects: [
        {
          target: { type: 'node', id: 'dist1' },
          op: 'merge_properties',
          payload: { ui: { highlighted: true, kind: 'path', pathIds: ['path-h1-h2-primary'], pathRanks: [1] } }
        },
        {
          target: { type: 'node', id: 'access1' },
          op: 'merge_properties',
          payload: { ui: { highlighted: true, kind: 'path', pathIds: ['path-h1-h2-primary'], pathRanks: [1] } }
        },
        {
          target: { type: 'edge', id: 'edge-dist-access1' },
          op: 'merge_properties',
          payload: { ui: { highlighted: true, kind: 'path', pathIds: ['path-h1-h2-primary'], pathRanks: [1] } }
        }
      ],
      meta: { source: 'mock', action: 'path' }
    }
  ],
  graphOverlays: []
});

const pathRelatedBase = makeTopologyContent('HQ topology - related path detail', 'fixed');
const pathRelatedTopology = makeTopologyContent('HQ topology - related path detail', 'fixed', {
  schema: 'topology.overlay.state.v1',
  base: buildTopologyOverlayBase(pathRelatedBase),
  revision: 1,
  selection: { nodeIds: [], edgeIds: [] },
  layers: [
    {
      id: 'layer-path-related-hq-cloud',
      kind: 'path',
      label: 'Hybrid cloud path detail',
      priority: 170,
      visible: true,
      path: {
        pathId: 'path-hq-cloud',
        resultScopeId: 'path_set_result:hq-cloud',
        requestedMode: 'auto',
        selectedMode: 'l3_reachability',
        evidenceLevel: 'state_validated',
        sourceSummary: 'Campus host H1',
        destinationSummary: 'Cloud service S1',
        renderDecision: {
          presentation: 'related_view',
          detailLevel: 'expanded',
          reason: 'A clean path-detail view is clearer than overlaying cross-site path hops on the current view.',
          graphOverlayId: 'layer-path-related-hq-cloud',
          layerId: 'layer-path-related-hq-cloud',
          relatedViewId: 'layer-path-related-hq-cloud'
        }
      },
      graphOverlay: {
        id: 'layer-path-related-hq-cloud',
        label: 'Hybrid cloud path detail',
        sourceViewId: viewId,
        viewId: 'path_detail',
        overlayKey: 'path-hq-cloud',
        graph: {
          graphId: 'mock:path-detail:hq-cloud',
          generatedAt: now,
          scope: siteScope,
          meta: {
            schema: 'topology.path.related_view.v1',
            pathId: 'path-hq-cloud',
            pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'],
            sourceViewId: viewId
          },
          nodes: [
            {
              id: 'host-h1',
              displayName: 'Host H1',
              labels: { level: 'endpoint', role: 'source' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'], ranks: [1, 2], primary: true, shared: true } }
            },
            {
              id: 'edge-gw',
              displayName: 'HQ Edge',
              labels: { level: 'device', role: 'edge' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'], ranks: [1, 2], primary: true, shared: true } }
            },
            {
              id: 'vpn-a',
              displayName: 'VPN Tunnel A',
              labels: { level: 'tunnel', role: 'primary' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary'], ranks: [1], primary: true, shared: false } }
            },
            {
              id: 'vpn-b',
              displayName: 'VPN Tunnel B',
              labels: { level: 'tunnel', role: 'secondary' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-secondary'], ranks: [2], primary: false, shared: false } }
            },
            {
              id: 'cloud-gw',
              displayName: 'Cloud Gateway',
              labels: { level: 'cloud_gateway' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'], ranks: [1, 2], primary: true, shared: true } }
            },
            {
              id: 'svc-s1',
              displayName: 'Service S1',
              labels: { level: 'service', role: 'destination' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'], ranks: [1, 2], primary: true, shared: true } }
            }
          ],
          edges: [
            {
              id: 'edge-h1-gw',
              fromId: 'host-h1',
              toId: 'edge-gw',
              directed: true,
              labels: { relationship: 'l3_next_hop' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'], ranks: [1, 2], primary: true, shared: true } }
            },
            {
              id: 'edge-gw-vpn-a',
              fromId: 'edge-gw',
              toId: 'vpn-a',
              directed: true,
              labels: { relationship: 'route_selected_tunnel' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary'], ranks: [1], primary: true, shared: false } }
            },
            {
              id: 'edge-gw-vpn-b',
              fromId: 'edge-gw',
              toId: 'vpn-b',
              directed: true,
              labels: { relationship: 'route_selected_tunnel' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-secondary'], ranks: [2], primary: false, shared: false } }
            },
            {
              id: 'edge-vpn-a-cloud',
              fromId: 'vpn-a',
              toId: 'cloud-gw',
              directed: true,
              labels: { relationship: 'vpn_attachment' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary'], ranks: [1], primary: true, shared: false } }
            },
            {
              id: 'edge-vpn-b-cloud',
              fromId: 'vpn-b',
              toId: 'cloud-gw',
              directed: true,
              labels: { relationship: 'vpn_attachment' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-secondary'], ranks: [2], primary: false, shared: false } }
            },
            {
              id: 'edge-cloud-svc',
              fromId: 'cloud-gw',
              toId: 'svc-s1',
              directed: true,
              labels: { relationship: 'cloud_route' },
              properties: { topologyPath: { pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'], ranks: [1, 2], primary: true, shared: true } }
            }
          ]
        },
        meta: {
          source: 'mock',
          path: {
            pathId: 'path-hq-cloud',
            pathIds: ['path-hq-cloud-primary', 'path-hq-cloud-secondary'],
            presentation: 'related_view',
            sourceViewId: viewId
          }
        }
      },
      meta: { source: 'mock', action: 'path' }
    }
  ],
  graphOverlays: []
});

const runtimeOverlay = (
  layerId: string,
  label: string,
  value: number | string,
  unit: string | undefined,
  effects: string[],
  tone: RuntimeTone
) => ({
  [layerId]: {
    layerId,
    kind: 'metric',
    catalogId: label.toLowerCase().includes('util')
      ? 'metrics.snmp.interface.utilization_pct'
      : 'metrics.snmp.device.cpu_pct',
    label,
    value,
    ...(unit ? { unit } : {}),
    effects,
    tone,
    updatedAt: new Date().toISOString()
  }
});

const snapshotResponse: TopologyV2ResolveResponse = {
  graph: {
    graphId: 'mock:runtime-overlay:snapshot',
    generatedAt: now,
    scope: siteScope,
    nodes: [
      {
        ...baseNodes[0],
        properties: {
          ui: {
            runtimeOverlays: runtimeOverlay('layer-snapshot-health', 'CPU', 39, '%', ['node_badge', 'node_ring', 'hover_rows'], 'success')
          }
        }
      },
      {
        ...baseNodes[1],
        properties: {
          ui: {
            runtimeOverlays: runtimeOverlay(
              'layer-snapshot-health',
              'CPU',
              76,
              '%',
              ['node_badge', 'node_color', 'hover_rows'],
              'warning'
            )
          }
        }
      },
      baseNodes[2]
    ],
    edges: [
      {
        id: 'edge-dist-access1',
        fromId: 'dist1',
        toId: 'access1',
        directed: false,
        labels: { relationship: 'physical_link' },
        properties: {
          ui: {
            runtimeOverlays: runtimeOverlay(
              'layer-snapshot-health',
              'Util',
              68,
              '%',
              ['edge_label', 'edge_color', 'hover_rows'],
              'warning'
            )
          }
        }
      },
      { id: 'edge-dist-access2', fromId: 'dist1', toId: 'access2', directed: false, labels: { relationship: 'physical_link' } }
    ]
  },
  legend: { nodeRules: [], edgeRules: [] },
  drilldowns: [],
  meta: { lastSeq: 10 }
};

const liveInitialResponse: TopologyV2ResolveResponse = {
  graph: {
    graphId: 'mock:runtime-overlay:live',
    generatedAt: now,
    scope: siteScope,
    nodes: baseNodes,
    edges: [
      { id: 'edge-dist-access1', fromId: 'dist1', toId: 'access1', directed: false, labels: { relationship: 'physical_link' } },
      { id: 'edge-dist-access2', fromId: 'dist1', toId: 'access2', directed: false, labels: { relationship: 'physical_link' } }
    ]
  },
  legend: { nodeRules: [], edgeRules: [] },
  drilldowns: [],
  meta: { lastSeq: 20 }
};

const pathBaseResponse: TopologyV2ResolveResponse = {
  graph: {
    graphId: 'mock:path-overlay:base',
    generatedAt: now,
    scope: siteScope,
    nodes: baseNodes,
    edges: [
      { id: 'edge-dist-access1', fromId: 'dist1', toId: 'access1', directed: false, labels: { relationship: 'physical_link' } },
      { id: 'edge-dist-access2', fromId: 'dist1', toId: 'access2', directed: false, labels: { relationship: 'physical_link' } }
    ]
  },
  legend: { nodeRules: [], edgeRules: [] },
  drilldowns: [],
  meta: { lastSeq: 30 }
};

const toneForPercent = (value: number): RuntimeTone => {
  if (value >= 90) {
    return 'critical';
  }
  if (value >= 70) {
    return 'warning';
  }
  return 'success';
};

const frame = (type: string, seq: number, data: Record<string, unknown>) =>
  JSON.stringify({
    type,
    viewId,
    scopeType: siteScope.scopeType,
    scopeId: siteScope.scopeId,
    seq,
    time: new Date().toISOString(),
    data
  });

const liveSamples = [
  { accessCpu: 42, distCpu: 51, link: 36 },
  { accessCpu: 74, distCpu: 58, link: 79 },
  { accessCpu: 93, distCpu: 81, link: 94 },
  { accessCpu: 63, distCpu: 49, link: 55 }
];

const TopologyRuntimeOverlayMockPage = () => {
  const { mockRoutes } = useConfig();
  const navigate = useNavigate();
  const resolveSnapshot = useCallback(async (_viewId: string, _request: TopologyV2ResolveRequest) => snapshotResponse, []);
  const resolveLive = useCallback(async (_viewId: string, _request: TopologyV2ResolveRequest) => liveInitialResponse, []);
  const resolvePath = useCallback(async (_viewId: string, _request: TopologyV2ResolveRequest) => pathBaseResponse, []);

  const streamLiveArtifact = useCallback(
    async (
      artifactId: string,
      overlayRevision: number,
      onEvent: (type: string, data: string) => void,
      signal: AbortSignal
    ): Promise<void> => {
      let seq = 100;
      let index = 0;

      const emitStatus = () => {
        onEvent(
          'overlay_status',
          frame('overlay_status', seq++, {
            artifactId,
            overlayRevision,
            layers: [
              {
                layerId: 'layer-live-utilization',
                kind: 'metric',
                catalogId: 'metrics.snmp.interface.utilization_pct',
                deliveryMode: 'live',
                status: 'active',
                message: 'Mock live stream is evaluating utilization samples.',
                updatedAt: new Date().toISOString(),
                overlayRevision
              }
            ]
          })
        );
      };

      const emitPatch = () => {
        const sample = liveSamples[index % liveSamples.length];
        index += 1;
        onEvent(
          'patch',
          frame('patch', seq++, {
            ops: [
              {
                target: { type: 'node', id: 'access1' },
                op: 'merge_properties',
                payload: {
                  ui: {
                    runtimeOverlays: runtimeOverlay(
                      'layer-live-utilization',
                      'CPU',
                      sample.accessCpu,
                      '%',
                      ['node_badge', 'node_ring', 'node_color', 'hover_rows'],
                      toneForPercent(sample.accessCpu)
                    )
                  }
                }
              },
              {
                target: { type: 'node', id: 'dist1' },
                op: 'merge_properties',
                payload: {
                  ui: {
                    runtimeOverlays: runtimeOverlay(
                      'layer-live-utilization',
                      'CPU',
                      sample.distCpu,
                      '%',
                      ['node_badge', 'node_ring', 'hover_rows'],
                      toneForPercent(sample.distCpu)
                    )
                  }
                }
              },
              {
                target: { type: 'edge', id: 'edge-dist-access1' },
                op: 'merge_properties',
                payload: {
                  ui: {
                    runtimeOverlays: runtimeOverlay(
                      'layer-live-utilization',
                      'Util',
                      sample.link,
                      '%',
                      ['edge_label', 'edge_color', 'hover_rows'],
                      toneForPercent(sample.link)
                    )
                  }
                }
              }
            ]
          })
        );
      };

      emitStatus();
      emitPatch();

      await new Promise<void>((resolve) => {
        const interval = window.setInterval(emitPatch, 1600);
        const cleanup = () => {
          window.clearInterval(interval);
          resolve();
        };
        if (signal.aborted) {
          cleanup();
          return;
        }
        signal.addEventListener('abort', cleanup, { once: true });
      });
    },
    []
  );

  if (!mockRoutes.includes('topology-runtime-overlays')) {
    return (
      <main className="auth-layout">
        <section className="auth-card">
          <header className="auth-card__header">
            <h1 className="auth-title">Mock routes disabled</h1>
            <p className="auth-subtitle">
              Enable the topology runtime overlay mock route to use this visual review page.
            </p>
          </header>
          <div className="auth-inline-panel">
            <p>
              Run locally with <code>VITE_MOCK_ROUTES=topology-runtime-overlays</code>, or use <code>VITE_MOCK_ROUTES=all</code>.
            </p>
          </div>
          <div>
            <button type="button" className="auth-submit" onClick={() => navigate('/login')}>
              Back to sign-in
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="runtime-overlay-mock">
      <header className="runtime-overlay-mock__header">
        <p className="runtime-overlay-mock__eyebrow">Topology runtime overlays</p>
        <h1>Snapshot and live overlay rendering</h1>
      </header>
      <section className="runtime-overlay-mock__grid" aria-label="Runtime overlay mock screens">
        <article className="runtime-overlay-mock__panel">
          <div className="runtime-overlay-mock__panel-header">
            <span>Path overlay</span>
            <strong>Existing graph path highlight</strong>
          </div>
          <TopologyArtifactView
            artifactId="mock-path-overlay-artifact"
            topology={pathOverlayTopology}
            resolveTopologyView={resolvePath}
            compact
          />
        </article>
        <article className="runtime-overlay-mock__panel">
          <div className="runtime-overlay-mock__panel-header">
            <span>Related view</span>
            <strong>Clean multi-path detail graph</strong>
          </div>
          <TopologyArtifactView
            artifactId="mock-path-related-artifact"
            topology={pathRelatedTopology}
            resolveTopologyView={resolvePath}
            compact
          />
        </article>
        <article className="runtime-overlay-mock__panel">
          <div className="runtime-overlay-mock__panel-header">
            <span>Snapshot</span>
            <strong>CPU and link utilization</strong>
          </div>
          <TopologyArtifactView
            artifactId="mock-snapshot-artifact"
            topology={snapshotTopology}
            resolveTopologyView={resolveSnapshot}
            compact
          />
        </article>
        <article className="runtime-overlay-mock__panel">
          <div className="runtime-overlay-mock__panel-header">
            <span>Live</span>
            <strong>Artifact stream updates</strong>
          </div>
          <TopologyArtifactView
            artifactId="mock-live-artifact"
            topology={liveTopology}
            resolveTopologyView={resolveLive}
            streamTopologyArtifact={streamLiveArtifact}
            compact
          />
        </article>
      </section>
    </main>
  );
};

export default TopologyRuntimeOverlayMockPage;
